// START A/B TEST PLGRWEB-1558 Live Sports Data Carousel
import { AbTest } from 'abTests/lib/AbTest';
import { UserStorage } from 'helpers/userStorage';
import HttpService from '../../services/HttpService';
import { trackAction } from 'helpers/trackingOM';

const SCORES_STORAGE_KEY = 'liveStorageScoresOn';

export default class LiveSportsDataCarousel extends AbTest {
    constructor() {
        super(CBS?.AbTestsConfigs?.modules?.LiveSportsDataCarousel);
        this.toggleButton = undefined;
        this.storage = undefined;
        this.scoresToggleState = true; // scores toggle is on by default
        this.scoresOnClass = 'scores-on';
        this.storageKey = SCORES_STORAGE_KEY;
        this.scoresToggleWrapper = undefined;
        this.toggleTooltip = undefined;
        this.toggleTooltipClose = undefined;
        this.refreshInterval = 120000; // interval to fetch live score data
        this.games = [];
        this.toggleCarouselOptions = undefined; // tracking params for the carousel w/ toggle
        this.implement();
    }

    /**
     * initialize live sports data test
     * @param options
     * @param carouselPos
     */
    init(options, carouselPos) {
        if (this.hasVariants()) {
            this.initEvents();

            if (options?.xhrResult?.displayScoresToggle) {
                // init scores toggle for first schedule carousel on the page
                this.#initToggle();
                // init live event refresh listener once
                this.#initEventRefresh();

                // set global options for this carousel tracking
                this.toggleCarouselOptions = options;
                this.toggleCarouselOptions.position = carouselPos;

                // if carousel doesn't have a position attribute, find its index
                if (this.toggleCarouselOptions.position === '') {
                    let carousels = document.querySelectorAll('.js-le-carousel');
                    carousels.forEach((carousel, index) => {
                        let toggleWrapper = carousel.querySelector('#scores-toggle-wrapper');
                        if (toggleWrapper) {
                            this.toggleCarouselOptions.position = index + 1;
                            return;
                        }
                    });
                }
            }
            this.updateScores();
        }
    }

    /**
     * initialize events for updating live sports data
     */
    initEvents() {
        let gameEvents = document.querySelectorAll(`[id*="game-"]`);
        gameEvents.forEach((gameEvent) => {
            let gameId = gameEvent.getAttribute('data-gameId');
            if (
                !this.games.includes(gameId) &&
                gameEvent.getAttribute('data-updateLive') === 'true'
            ) {
                this.games.push(gameId);
            }
        });
        // update scores to show for any newly initialized events in the DOM
        this.updateScores();
    }

    /**
     * initialize the live event refresh interval
     */
    #initEventRefresh() {
        if (this.games.length > 0) {
            // init timeout to refresh live events scores every 60 sec
            setInterval(async () => {
                let response = await this.#fetchLiveResults();
                if (response.success === true) {
                    // update home & away team score attributes
                    this.setScores(response.data?.gameData);
                }
            }, this.refreshInterval);
        }
    }

    /**
     * request to get live data for specified game IDs
     */
    #fetchLiveResults() {
        const http = new HttpService();
        let gameIds = this.games.toString();
        const liveResultsUrl = '/carousels/liveResults/?gameIds=' + gameIds;
        return http.doGet(liveResultsUrl, {});
    }

    /**
     * get page URL to distinguish if on homepage or sports hub
     * @returns {string}
     */
    getPageUrl() {
        let path = window.location.pathname;
        // strip trailing slash from path
        path = path.endsWith('/') ? path.slice(0, -1) : path;

        return path.split('/').pop();
    }

    /**
     * initialize scores toggle and event listener
     */
    #initToggle() {
        this.toggleButton = document.querySelector('#switch-scores-toggle');
        this.initStorage();
        this.initToggleState();
        this.initToggleTooltip();
        this.#setupToggleEvents();
    }

    /**
     * init local storage to persist scores toggle on/off
     */
    initStorage() {
        this.storage = new UserStorage();
    }

    /**
     * init toggle state if ON from local storage
     */
    initToggleState() {
        // read from the storage for the state of toggle
        let storageToggleState = this.storage.get(this.storageKey) ?? true;

        if (storageToggleState) {
            this.toggleButton.classList.add(this.scoresOnClass);
            this.scoresToggleState = storageToggleState;
        } else {
            this.storage.store(this.storageKey, this.scoresToggleState);
        }

        // update toggle state for global tracking
        utag_data.toggleEnabled = Number(this.scoresToggleState).toString();
    }

    /**
     * initialize tooltip to display for users seeing the toggle for the first time
     */
    initToggleTooltip() {
        let currentPath = this.getPageUrl();
        let tooltipStoreKey = currentPath + '-toggleTooltip';
        this.scoresToggleWrapper = document.getElementById('scores-toggle-wrapper');

        // check if user should see tooltip for scores toggle
        let hasSeenTooltip = this.storage.get(tooltipStoreKey) ?? false;

        if (!hasSeenTooltip && this.scoresToggleWrapper) {
            this.toggleTooltip = this.scoresToggleWrapper.querySelector('.tooltip');

            if (this.toggleTooltip) {
                // show tooltip for scores toggle
                this.toggleTooltip.classList.remove('hide-tooltip');
                // set timeout to disappear after 6 sec
                this.setTooltipTimeout();
                // add event listener to disappear if user closes by clicking X
                this.toggleTooltipClose = this.toggleTooltip.querySelector('.tooltip-close-out');
                this.toggleTooltipClose.addEventListener('click', () => {
                    this.closeTooltip();
                });
            }
            // store that user has already seen tooltip and won't show again
            this.storage.store(tooltipStoreKey, true);
        }
    }

    /**
     * set timeout to auto hide tooltip after 6 seconds
     */
    setTooltipTimeout() {
        setTimeout(() => {
            this.closeTooltip();
        }, 6000);
    }

    /**
     * hide tooltip from displaying
     */
    closeTooltip() {
        this.toggleTooltip?.classList.add('hide-tooltip');
    }

    /**
     * display scores if toggle state is on, otherwise display dash to hide
     */
    updateScores() {
        this.games.forEach((gameEvent) => {
            let { homeScores, awayScores, gameTimes } = this.getTeamScoreElements(gameEvent);

            // loop through the score elements since there can be duplicate events in multiple carousels
            for (let i = 0; i < homeScores.length; i++) {
                // display home team score if toggle state enabled
                this.setInnerHTML('data-teamScore', homeScores[i]);
                // display away team score if toggle state enabled
                this.setInnerHTML('data-teamScore', awayScores[i]);
                // update game time display regardless of toggle state
                this.setInnerHTML('data-gameTime', gameTimes[i], false);
            }
        });
    }

    /**
     * set inner HTML for given element
     * @param key
     * @param elem
     */
    setInnerHTML(key, elem, validate = true) {
        if (elem) {
            if (validate) {
                elem.innerHTML = this.scoresToggleState ? elem.getAttribute(key) : '-';
            } else {
                elem.innerHTML = elem.getAttribute(key);
            }
        }
    }

    /**
     * set data attribute for given element
     * @param key
     * @param value
     * @param elem
     */
    setDataAttribute(key, value, elem) {
        if (elem && value) {
            elem.setAttribute(key, value);
        }
    }

    /**
     * method to parse existing value to string
     * @param value
     * @returns {string}
     */
    parseToString(value) {
        if (value !== undefined && value !== null) {
            value = value.toString();
        }
        return value;
    }

    /**
     * get elements to display current home/away scores for specified game event
     */
    getTeamScoreElements(gameId) {
        let teamScores = {};
        let homeTeamScores = [];
        let awayTeamScores = [];
        let gameTimeDisplays = [];
        let gameElements = document.querySelectorAll('#game-' + gameId);

        if (gameElements) {
            gameElements.forEach((gameElem) => {
                // get the home/away score elements for this game
                homeTeamScores.push(gameElem.querySelector('.team-score.home'));
                awayTeamScores.push(gameElem.querySelector('.team-score.away'));
                // get game time display element
                gameTimeDisplays.push(gameElem.querySelector('.game-time-elapsed'));
            });

            teamScores = {
                homeScores: homeTeamScores,
                awayScores: awayTeamScores,
                gameTimes: gameTimeDisplays,
            };
        }
        return teamScores;
    }

    /**
     * set home & away score data attributes
     * @param gameData
     */
    setScores(gameData) {
        Object.entries(gameData).forEach(([gameId, data]) => {
            // get home & away refreshed team scores from API call
            let homeTeamScore = this.parseToString(data.homeTeamScore);
            let awayTeamScore = this.parseToString(data.awayTeamScore);
            let gameTimeDisplayText = data.gameTimeDisplayText;

            let { homeScores, awayScores, gameTimes } = this.getTeamScoreElements(gameId);

            // loop through the score elements since there can be duplicate events in multiple carousels
            for (let i = 0; i < homeScores.length; i++) {
                this.setDataAttribute('data-teamScore', homeTeamScore, homeScores[i]);
                this.setDataAttribute('data-teamScore', awayTeamScore, awayScores[i]);
                this.setDataAttribute('data-gameTime', gameTimeDisplayText, gameTimes[i]);
            }
        });

        // display the refreshed scores on the carousel
        this.updateScores();
    }

    /**
     * setup toggle event listener to switch scores data on/off
     */
    #setupToggleEvents() {
        // set the toggle events
        this.toggleButton?.addEventListener('click', () => {
            this.toggle();
            this.trackToggleSwitch();
        });
    }

    /**
     * switch toggle on/off and update value in local storage to persist during user's session
     */
    toggle() {
        if (this.scoresToggleState) {
            // toggle state is currently on, turn it off
            this.toggleButton.classList.remove(this.scoresOnClass);
        } else {
            // toggle state is currently off, turn it on
            this.toggleButton.classList.add(this.scoresOnClass);
        }
        this.scoresToggleState = !this.scoresToggleState;
        this.storage.store(this.storageKey, this.scoresToggleState);

        // update toggle state for global tracking
        utag_data.toggleEnabled = Number(this.scoresToggleState).toString();

        this.updateScores();
        // hide tooltip if it was displayed
        this.closeTooltip();
        // if toggle was turned on, init live score refreshing every 30 sec
        if (this.scoresToggleState) this.#initEventRefresh();
    }

    /**
     * track action for user switching toggle on/off
     */
    trackToggleSwitch() {
        let xhrResult = this.toggleCarouselOptions?.xhrResult;

        let obj = {
            carouselId: xhrResult?.id,
            carouselPresentationStyle: xhrResult?.carouselPresentationStyle,
            carouselContentType: 'event',
            carouselModel: 'schedule',
            rowHeaderTitle: xhrResult?.title,
            posRowNum: this.toggleCarouselOptions?.position,
        };
        trackAction('trackLiveScoreToggle', obj);
    }
}

/**
 * method to fetch the toggle scores on/off status for global tracking
 * @returns {*}
 */
export const getToggleEnabledStatus = () => {
    const storage = new UserStorage();
    let toggleEnabled = storage.get(SCORES_STORAGE_KEY);

    if (toggleEnabled !== undefined) {
        toggleEnabled = Number(toggleEnabled).toString();
    }
    return toggleEnabled;
};
// END A/B TEST PLGRWEB-1558 Live Sports Data Carousel
